// frontend/src/components/admin/Dashboard.js
// Add a new RecentOrders component:

import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
} from "@mui/material";
import { adminAPI } from "../../services/api";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AdminStats from "./AdminStats";
import TransactionsChart from "./TransactionsChart";
import ResellerTable from "./ResellerTable";

// New component for Recent Orders
const RecentOrders = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentOrders = async () => {
      try {
        setLoading(true);
        const response = await adminAPI.getRecentOrders();
        setOrders(response.data);
      } catch (error) {
        console.error("Failed to fetch recent orders:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentOrders();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={30} />
      </Box>
    );
  }


  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("esim.orders.table.orderNo")}</TableCell>
            <TableCell>{t("esim.orders.table.reseller")}</TableCell>
            <TableCell>{t("esim.orders.table.status")}</TableCell>
            <TableCell>{t("esim.orders.table.package")}</TableCell>
            <TableCell>{t("esim.orders.table.purchaseDate")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order) => (
            <TableRow key={order.id}>
              <TableCell>{order.orderNo}</TableCell>
              <TableCell>
                <Typography
                  component="a"
                  sx={{
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={() => navigate(`/admin/resellers/${order.userId}`)}
                >
                  {order.username}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={t(`esim.orders.statuses.${order.status}`, {
                    defaultValue: order.status.toUpperCase(),
                  })}
                  color={
                    order.status === "IN_USE"
                      ? "success"
                      : order.status === "GOT_RESOURCE"
                      ? "info"
                      : order.status === "SUSPENDED"
                      ? "warning"
                      : "error"
                  }
                  size="small"
                />
              </TableCell>
              <TableCell>{order.packageName}</TableCell>
              <TableCell>
                {order.createdAt
                  ? new Date(order.createdAt).toLocaleString()
                  : t("common.noData")}
              </TableCell>
            </TableRow>
          ))}
          {orders.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                {t("esim.orders.noOrders")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const AdminDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalResellers: 0,
    totalTransactions: 0,
    totalCreditsIssued: 0,
    totalESIMOrders: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await adminAPI.getSystemStatistics();
        setStats(response.data);
      } catch (err) {
      } finally {
      }
    };

    fetchStats();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t("admin.dashboard.title")}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <AdminStats stats={stats} />
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6" gutterBottom>
                {t("admin.dashboard.recentTransactions")}
              </Typography>
              <Button
                size="small"
                onClick={() => navigate("/admin/transactions")}
              >
                {t("common.actions.viewAll")}
              </Button>
            </Box>
            <TransactionsChart />
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("admin.dashboard.topResellers")}
            </Typography>
            <ResellerTable />
          </Paper>
        </Grid>

        {/* New section for recent orders */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6" gutterBottom>
                {t("admin.dashboard.recentOrders")}
              </Typography>
              <Button
                size="small"
                onClick={() => navigate("/admin/esim-orders")}
              >
                {t("common.actions.viewAll")}
              </Button>
            </Box>
            <RecentOrders />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
